import { groupBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  ArrayField,
  Datagrid,
  DateField,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  TextField,
  useDataProvider,
  useListContext,
  useNotify,
  usePermissions,
} from 'react-admin';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId, getUserId } from '../../lib';

const printCell = (record) => ({
  '@media print': {
    padding: 1,
    margin: 1,
    '& span': {
      fontSize: 9,
      color: '#000000'
    },
    borderBottomColor: '#000000',
  },
});

const ConsistencyPanel = () => {
  const { filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { permissions } = usePermissions();
  const isAdmin = permissions && permissions.includes(PERMISSIONS.ADMIN);
  // Botão de auditoria/recálculo é exclusivo de admin (backend exige scope isAdmin).
  if (!isAdmin) return null;

  const check = async () => {
    setLoading(true);
    try {
      const res = await dataProvider.getList('chains/chainId/reports/partner-values/reconciliation', {
        filter: { from: filterValues.from, to: filterValues.to },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });
      setResult(res.data[0]);
    } catch (e) {
      notify('Erro ao conferir consistência', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const recompute = async (customerId) => {
    try {
      await dataProvider.create(`chains/chainId/customers/${customerId}/rebuild-points-attribution`, { data: {} });
      notify('Recalculado/agendado. Confira novamente.', { type: 'success' });
    } catch (e) {
      notify('Erro ao recalcular', { type: 'error' });
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <button type="button" onClick={check} disabled={loading}>
        {loading ? 'Conferindo…' : 'Conferir consistência (período)'}
      </button>
      {result && (
        <div style={{ marginTop: 8 }}>
          {result.consistent ? (
            <span style={{ color: 'green' }}>
              ✅ Bateu — valores por parceiro == cupons validados (R$ {Number(result.couponsTotal).toFixed(2)})
            </span>
          ) : (
            <>
              <div style={{ color: 'red' }}>
                ❌ Diferença R$ {Number(result.diff).toFixed(2)} (cupons {Number(result.couponsTotal).toFixed(2)} × parceiros {Number(result.partnerValuesTotal).toFixed(2)})
              </div>
              <table>
                <thead>
                  <tr><th>Cupom</th><th>Cliente</th><th>Δ</th><th></th></tr>
                </thead>
                <tbody>
                  {(result.mismatches || []).map((m) => (
                    <tr key={m.couponId}>
                      <td>{m.barcode || m.couponId}</td>
                      <td>{m.customerId}</td>
                      <td>R$ {Number(m.delta).toFixed(2)}</td>
                      <td><button type="button" onClick={() => recompute(m.customerId)}>recalcular</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ReportPartnerValuesList = ({ Panel }) => {
  const props = useListContext();

  let data = [], summaryData = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo) {
      data = reportInfo.data;
      summaryData = [reportInfo.summaryData];
    }
  }

  return (
    <>
      <ConsistencyPanel />
      <SummaryData />
      <Datagrid bulkActionButtons={false} rowSx={printCell} data={data} total={1}>
        <ReferenceField label="Parceiro" source="id" reference="places" link={false} sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ArrayField source="products" label="Produtos" sortable={false}>
          <Datagrid bulkActionButtons={false} >
            <ReferenceField label="Produtos" emptyText="Cashback" source="id" reference="chains/chainId/products" link={false} sortable={false}>
              <TextField source="name" />
            </ReferenceField>
            <NumberField
              source="partner_value"
              label="Valor"
              options={{ style: 'currency', currency: 'BRL' }}
              textAlign="center"
              sortable={false}
            />
          </Datagrid>
        </ArrayField>
        <NumberField
          source="partner_value"
          label="Valores"
          options={{ style: 'currency', currency: 'BRL' }}
          textAlign="center"
          sortable={false}
        />
      </Datagrid>
    </>
  )
}

const partnerValuesExporter = async (data, fetchRelatedRecords) => {
  let reportInfo = data[0].data;

  reportInfo = [].concat(...reportInfo.map(item => item.products.map(p => ({
    id: item.id,
    partner_value: item.partner_value,
    product_id: p.id,
    product_value: p.partner_value,
    place_id: item.place_id,
  }))));

  const relations = [
    { field: 'id', resource: 'places' },
    { field: 'place_id', resource: 'chains/chainId/places' },
    { field: 'product_id', resource: 'chains/chainId/products' },
  ];

  const fetchData = await fetchRelatedRecords(relations, reportInfo);

  const getData = (resource) => {
    return fetchData.find(({ resource: res }) => res === resource)?.results || [];
  }

  reportInfo = reportInfo.map(item => {
    const { id: itemId, partner_value: valor_total, place_id, product_id, product_value } = item;
    const place = getData('places').find(({ id }) => id === itemId);
    const product = getData('chains/chainId/products').find(({ id }) => id === product_id);
    const filterPlace = getData('chains/chainId/places').find(({ id }) => id === place_id);

    return {
      local: place ? place?.name : 'Não identificado',
      valor_total,
      produto: product ? product.name : 'Não identificado',
      valor_produto: product_value,
      filter_place: filterPlace ? filterPlace.name : 'Não identificado',
    }
  });

  reportInfo = groupBy(reportInfo, 'local');
  reportInfo = Object.keys(reportInfo).map(key => {
    const item = reportInfo[key];
    return {
      local_pagador: key,
      local_recebedor: item[0].filter_place,
      valor_total: item[0].valor_total,
      produtos: item.map(i => ({ nome: i.produto, valor: i.valor_produto })),
    };
  });

  return reportInfo;
}

const SummaryData = () => {
  const props = useListContext();

  let summaryRows = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo?.summaryData) {
      const s = reportInfo.summaryData;
      const num = (v) => parseFloat(v) || 0;
      summaryRows = [
        { id: 'cashback', label: 'Cashback', bruto: num(s.total_cashback), liquido: num(s.net_total_cashback) },
        { id: 'produtos', label: 'Produtos', bruto: num(s.total_product), liquido: num(s.net_total_product) },
        { id: 'total', label: 'Total', bruto: num(s.total_cashback) + num(s.total_product), liquido: num(s.total_value) },
      ];
    }
  }

  return (
    <Datagrid bulkActionButtons={false} rowSx={printCell} style={{ marginBottom: 60 }} data={summaryRows} total={summaryRows.length}>
      <TextField source="label" label="" sortable={false} />
      <NumberField
        source="bruto"
        label="Bruto"
        emptyText="R$ 0,00"
        options={{ style: 'currency', currency: 'BRL' }}
        textAlign="center"
        sortable={false}
      />
      <NumberField
        source="liquido"
        label="Líquido"
        emptyText="R$ 0,00"
        options={{ style: 'currency', currency: 'BRL' }}
        textAlign="center"
        sortable={false}
      />
    </Datagrid>
  )
}

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  filterValues.userId = getUserId();

  return (
    <>
      <Datagrid bulkActionButtons={false} rowSx={printCell} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
        <ReferenceField label="Usuário" source="userId" reference="employees" emptyText="Não definido" textAlign="center" filter={{ type: 'place' }}>
          <TextField source="login" />
        </ReferenceField>
      </Datagrid>
      <Datagrid bulkActionButtons={false} rowSx={printCell} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
        <DateField source="from" textAlign="center" label="De" showTime sortable={false} locales="pt-BR" />
        <DateField source="to" textAlign="center" label="Até" showTime sortable={false} locales="pt-BR" />
        <DateField source="generatedReportAt" textAlign="center" label="Gerado" showTime sortable={false} locales="pt-BR" />
      </Datagrid>
    </>
  );
}

const EmployeesFilterInput = (props) => {
  const { filterValues, setFilters } = useListContext();
  const dataProvider = useDataProvider();
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (filterValues.partnerId) {
      dataProvider.getList('employees', {
        sort: { field: 'name', order: 'ASC' },
        filter: { placeId: filterValues.partnerId, type: 'place' }
      })
        .then(res => {
          const dataFormated = res.data.map((e) => ({ id: e.id, name: e.name }));
          setEmployees(dataFormated.length ? dataFormated : [{ id: null, name: "Todos" }]);
        });
    } else {
      setEmployees([]);
      if (filterValues.employeeIds) {
        const { employeeIds, ...rest } = filterValues;
        setFilters(rest);
      }
    }
  }, [filterValues.partnerId]);

  return (
    <SelectArrayInput
      {...props}
      label="Funcionários"
      source="employeeIds"
      choices={employees}
      disabled={!filterValues.partnerId}
      style={{ minWidth: 180, display: filterValues.partnerId ? 'flex' : 'none' }}
    />
  );
};

const getReportFilters = () => {
  const filters = [
    <BetweenDatesTimesInput key="dates" format={'YYYY-MM-DD HH:mm'} alwaysOn />,
  ];

  if (!getPlaceId()) {
    filters.push(
      <ReferenceInput
        key="partnerId"
        source="partnerId"
        reference="places"
        filter={{ chainId: getChainId() }}
        perPage={null}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <SelectInput
          label="Local"
          emptyText="Todos"
          style={{ minWidth: 180 }}
          optionText={'name'} />
      </ReferenceInput>,
      <EmployeesFilterInput key="employeeIds" source="employeeIds" alwaysOn />
    );
  }

  return filters;
}


const ReportPartnerValues = (props) => {
  return (
    <Permission permission={PERMISSIONS.PARTNER_VALUES_REPORT}>
      <ReportList periodRequired
        {...props}
        basePath="report-partner-values"
        title="Relatório de valores por parceiros"
        resource="chains/chainId/reports/partner-values"
        filters={getReportFilters()}
        sort={{ field: 'id', order: 'ASC' }}
        perPage={999999}
        showPagination={false}
        filterDefaultValues={{
          from: moment().startOf('day').toISOString(),
          to: moment().endOf('day').toISOString(),
          partnerId: getPlaceId(),
          employeeIds: getPlaceId() ? [getUserId()] : null
        }}
        bulkActionButtons={false}
        customExporter={partnerValuesExporter}
        filterTablePrint={<FilterTablePrint />}
        showFilterLabel={false}
        landscape={false}
        disableSummaryPrint={false}
        titleOnPrint={"Relatório de valores por parceiros"}
        noPadding
        printContent={() => (
          <>
            <FilterTablePrint />
            <SummaryData />
          </>
        )}
      >
        <ReportPartnerValuesList />
      </ReportList>
    </Permission >
  );
};

export default ReportPartnerValues;